import React, { useEffect, useRef, useState } from 'react'
import { useNotificationsHomeContext } from '../notifications/context'

const Menu = ({
  children,
  open = false,
  anchorEl = null,
  anchorOrigin = { vertical: 'bottom', horizontal: 'left' },
  transformOrigin = { vertical: 'top', horizontal: 'left' },
  className = '',
  sx = {},
  onClose,
  ...props
}) => {
  const {
    data: { showConfig, preferenceMode }
  } = useNotificationsHomeContext()

  const menuRef = useRef(null)
  const [isEntering, setIsEntering] = useState(false)
  const [isExiting, setIsExiting] = useState(false)
  const [shouldRender, setShouldRender] = useState(false)

  useEffect(() => {
    if (open) {
      setShouldRender(true)

      const enterTimer = setTimeout(() => {
        setIsEntering(true)
        setIsExiting(false)
      }, 10)
      return () => clearTimeout(enterTimer)
    } else if (shouldRender) {
      setIsEntering(false)
      setIsExiting(true)

      const exitTimer = setTimeout(() => {
        setShouldRender(false)
        setIsExiting(false)
      }, 250)
      return () => clearTimeout(exitTimer)
    }
  }, [open, shouldRender])

  useEffect(() => {
    if (!open) {
      return
    }

    // Without this, the modal closes when the overlay is clicked
    if (preferenceMode === 'modal' && showConfig) {
      return
    }

    const handleClickAway = (event) => {
      const target = event.target
      const menuNode = menuRef.current
      const isInsideMenu = menuNode?.contains(target)
      const isInsideAnchor = anchorEl?.contains(target)

      const isPortalClick =
        target.closest('[data-radix-popper-content-wrapper]') ||
        target.closest('[data-slot="popover-content"]') ||
        target.closest('[role="dialog"]') ||
        target.closest('[data-radix-portal]')

      if (!isInsideMenu && !isInsideAnchor && !isPortalClick) {
        onClose(event, 'backdropClick')
      }
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose(event, 'escapeKeyDown')
      }
    }

    document.addEventListener('mousedown', handleClickAway)
    document.addEventListener('touchstart', handleClickAway)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handleClickAway)
      document.removeEventListener('touchstart', handleClickAway)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, anchorEl, onClose])

  const getMenuStyles = () => {
    const styles = {
      position: 'fixed',
      zIndex: 1300,
      outline: 'none'
    }

    if (anchorEl) {
      const rect = anchorEl.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      const menuNode = menuRef.current
      const estimatedMenuWidth = menuNode ? menuNode.offsetWidth : 200
      const estimatedMenuHeight = menuNode ? menuNode.offsetHeight : 100

      let anchorTop = rect.top
      let anchorLeft = rect.left

      if (anchorOrigin.vertical === 'bottom') {
        anchorTop = rect.bottom
      } else if (anchorOrigin.vertical === 'center') {
        anchorTop = rect.top + rect.height / 2
      }

      if (anchorOrigin.horizontal === 'right') {
        anchorLeft = rect.right
      } else if (anchorOrigin.horizontal === 'center') {
        anchorLeft = rect.left + rect.width / 2
      }

      let transformX = '0%'
      let transformY = '0%'
      let finalAnchorLeft = anchorLeft
      let finalAnchorTop = anchorTop

      if (transformOrigin.horizontal === 'center') {
        transformX = '-50%'

        if (anchorLeft - estimatedMenuWidth / 2 < 0) {
          transformX = '0%'
          finalAnchorLeft = 8
        } else if (anchorLeft + estimatedMenuWidth / 2 > viewportWidth) {
          transformX = '-100%'
          finalAnchorLeft = viewportWidth - 8
        }
      } else if (transformOrigin.horizontal === 'right') {
        transformX = '-100%'

        if (anchorLeft - estimatedMenuWidth < 0) {
          transformX = '0%'
          finalAnchorLeft = 8
        }
      } else {
        transformX = '0%'

        if (anchorLeft + estimatedMenuWidth > viewportWidth) {
          transformX = '-100%'
          finalAnchorLeft = viewportWidth - 8
        }
      }

      if (transformOrigin.vertical === 'center') {
        transformY = '-50%'

        if (anchorTop - estimatedMenuHeight / 2 < 0) {
          transformY = '0%'
          finalAnchorTop = 8
        } else if (anchorTop + estimatedMenuHeight / 2 > viewportHeight) {
          transformY = '-100%'
          finalAnchorTop = viewportHeight - 8
        }
      } else if (transformOrigin.vertical === 'bottom') {
        transformY = '-100%'

        if (anchorTop - estimatedMenuHeight < 0) {
          transformY = '0%'
          finalAnchorTop = 8
        }
      } else {
        transformY = '0%'

        if (anchorTop + estimatedMenuHeight > viewportHeight) {
          transformY = '-100%'
          finalAnchorTop = viewportHeight - 8
        }
      }

      styles.top = finalAnchorTop
      styles.left = finalAnchorLeft
      styles.transform = `translate(${transformX}, ${transformY})`

      let originX = '0%'
      let originY = '0%'

      if (transformOrigin.horizontal === 'center') {
        originX = '50%'
      } else if (transformOrigin.horizontal === 'right') {
        originX = '100%'
      }

      if (transformOrigin.vertical === 'center') {
        originY = '50%'
      } else if (transformOrigin.vertical === 'bottom') {
        originY = '100%'
      }

      styles.transformOrigin = `${originX} ${originY}`
    }

    return styles
  }

  if (!shouldRender) {
    return null
  }

  const menuStyles = getMenuStyles()

  // Calculate animation styles based on state
  const getAnimationStyles = () => {
    const baseStyles = {
      transition: 'opacity 250ms cubic-bezier(0.4, 0, 0.2, 1)',
      visibility: 'visible'
    }

    if (isEntering) {
      return {
        ...baseStyles,
        opacity: 1
      }
    } else if (isExiting) {
      return {
        ...baseStyles,
        opacity: 0
      }
    } else {
      // Initial state
      return {
        ...baseStyles,
        opacity: 0
      }
    }
  }

  const getPaperAnimationStyles = () => {
    // Calculate transform origin for animation scaling
    let originX = '0%'
    let originY = '0%'

    if (transformOrigin.horizontal === 'center') {
      originX = '50%'
    } else if (transformOrigin.horizontal === 'right') {
      originX = '100%'
    }

    if (transformOrigin.vertical === 'center') {
      originY = '50%'
    } else if (transformOrigin.vertical === 'bottom') {
      originY = '100%'
    }

    const baseStyles = {
      transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)',
      transformOrigin: `${originX} ${originY}`
    }

    if (isEntering) {
      return {
        ...baseStyles,
        transform: 'scale(1)'
      }
    } else if (isExiting) {
      return {
        ...baseStyles,
        transform: 'scale(0.95)'
      }
    } else {
      // Initial state
      return {
        ...baseStyles,
        transform: 'scale(0.95)'
      }
    }
  }

  const animationStyles = getAnimationStyles()
  const paperAnimationStyles = getPaperAnimationStyles()

  return (
    <div
      ref={menuRef}
      className={className}
      style={{
        ...menuStyles,
        ...animationStyles,
        ...sx
      }}
      role='menu'
      {...props}
    >
      <div
        style={{
          borderRadius: '10px',
          ...paperAnimationStyles
        }}
      >
        <div style={{ position: 'relative' }}>{children}</div>
      </div>
    </div>
  )
}

export default Menu
